import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./employee.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";
import deleteIcon from "../../assets/delete_icon.png";
import eyeIconCross from "../../assets/eye_icon_cross.png";
import eyeIcon from "../../assets/eye_icon.png";

interface Employee {
  _id: string;
  email: string;
  password: string;
  role: string;
  name: string;
  phoneNumber: string;
  gender: string;
  __v: number;
}

const Employee = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true); // Add loading state
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(
    null
  );
  const [addModalVisible, setAddModalVisible] = useState<boolean>(false); // Add Employee Modal
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    phoneNumber: "",
    gender: "",
  }); // New Employee state
  const navigate = useNavigate();
  const isInitialRender = useRef(true);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Fetch user details and employees
  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-sales-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllEmployees(token);
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      alert("Session expired or invalid token! Redirecting to login.");
      handleLogout();
    }
  };

  const fetchAllEmployees = async (token: string) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-sales-employees`,
        config
      );
      setEmployees(response.data.employees);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    } finally {
      setLoading(false); // Stop the loading indicator once API responds
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleDelete = async () => {
    if (!employeeToDelete) return;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(
        `https://api.fitoja.in/api/delete-sales-employee/${employeeToDelete.email}`,
        config
      );
      fetchAllEmployees(token);
      closeDeleteModal();
    } catch (error) {
      console.error("Failed to delete employee:", error);
      alert("Failed to delete employee. Please try again.");
    }
  };

  const openDeleteModal = (employee: Employee) => {
    setEmployeeToDelete(employee);
    setDeleteModalVisible(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalVisible(false);
    setEmployeeToDelete(null);
  };

  // Add new employee API call
  const handleAddEmployee = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(
        `https://api.fitoja.in/api/create-sales-employee`,
        newEmployee,
        config
      );
      fetchAllEmployees(token);
      closeAddModal();
    } catch (error) {
      console.error("Failed to add employee:", error);
      alert("Failed to add employee. Please try again.");
    }
  };

  const openAddModal = () => {
    setAddModalVisible(true);
  };

  const closeAddModal = () => {
    setAddModalVisible(false);
    setNewEmployee({
      name: "",
      email: "",
      password: "",
      role: "",
      phoneNumber: "",
      gender: "",
    });
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  return (
    <div className="employee-container">
      {/* Loading indicator */}
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          {employees.length > 0 ? (
            <h2 style={{ marginTop: "110px", marginBottom: "0px" }}>
              Employee Portal
            </h2>
          ) : (
            ""
          )}

          <div className="add-employee" onClick={openAddModal}>
            <span>Add Employee</span>
          </div>

          <div className="employee-list">
            {employees.length > 0 ? (
              employees.map((employee) => (
                <div key={employee._id} className="employee-card">
                  <div className="employee-details">
                    <p>
                      <strong>Name:</strong> <span>{employee.name}</span>
                    </p>
                    <p>
                      <strong>Email:</strong> <span>{employee.email}</span>
                    </p>
                    <p>
                      <strong style={{ paddingRight: "5px" }}>Password:</strong>
                      <span>
                        {showPassword
                          ? employee.password
                          : "*".repeat(employee.password.length)}
                        <button
                          className="eye-icon-btn"
                          onClick={togglePasswordVisibility}
                          style={{ marginLeft: "10px" }}
                        >
                          {showPassword ? (
                            <img className="eye-econ-img" src={eyeIconCross} />
                          ) : (
                            <img className="eye-econ-img" src={eyeIcon} />
                          )}
                        </button>
                      </span>
                    </p>
                    <p>
                      <strong>Role:</strong> <span>{employee.role}</span>
                    </p>
                    <p>
                      <strong>Phone:</strong>{" "}
                      <span>{employee.phoneNumber}</span>
                    </p>
                    <p>
                      <strong>Gender:</strong> <span>{employee.gender}</span>
                    </p>
                  </div>
                  <div
                    className="delete-icon"
                    onClick={() => openDeleteModal(employee)}
                  >
                    <img className="img" src={deleteIcon}></img>
                  </div>
                </div>
              ))
            ) : (
              <p></p>
            )}
          </div>

          {deleteModalVisible && (
            <div className="delete-modal">
              <div className="delete-modal-content">
                <h2>Delete Confirmation</h2>
                <p>Are you sure you want to delete this employee?</p>
                <span style={{ display: "flex", gap: "20px" }}>
                  <button onClick={closeDeleteModal} className="cancel-button">
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="confirm-delete-button"
                  >
                    Yes, Delete
                  </button>
                </span>
              </div>
            </div>
          )}

          {addModalVisible && (
            <div className="add-modal">
              <div className="add-modal-content">
                <h2>Add Employee</h2>
                <form>
                  <input
                    type="text"
                    placeholder="Name"
                    value={newEmployee.name}
                    onChange={(e) =>
                      setNewEmployee({ ...newEmployee, name: e.target.value })
                    }
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={newEmployee.email}
                    onChange={(e) =>
                      setNewEmployee({ ...newEmployee, email: e.target.value })
                    }
                    required
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={newEmployee.password}
                    onChange={(e) =>
                      setNewEmployee({
                        ...newEmployee,
                        password: e.target.value,
                      })
                    }
                    required
                  />
                  <select
                    value={newEmployee.role}
                    onChange={(e) =>
                      setNewEmployee({ ...newEmployee, role: e.target.value })
                    }
                    required
                  >
                    <option value="" disabled>
                      Select Role
                    </option>
                    <option value="Owner">Owner (Full Access)</option>
                    <option value="Developer">Developer (Full Access)</option>
                    <option value="Sales">Sales (Limited Access)</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Phone Number"
                    value={newEmployee.phoneNumber}
                    onChange={(e) =>
                      setNewEmployee({
                        ...newEmployee,
                        phoneNumber: e.target.value,
                      })
                    }
                    required
                  />
                  <select
                    value={newEmployee.gender}
                    onChange={(e) =>
                      setNewEmployee({ ...newEmployee, gender: e.target.value })
                    }
                    required
                  >
                    <option value="" disabled>
                      Select Gender
                    </option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>

                  <button
                    type="button"
                    onClick={handleAddEmployee}
                    className="add-employee-button"
                  >
                    Add Employee
                  </button>
                  <button
                    type="button"
                    onClick={closeAddModal}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Employee;
